<template>
  <div class="multi-area-chart">
    <div v-if="loading" class="chart-status">加载趋势数据中…</div>
    <div v-else-if="error" class="chart-status error">{{ error }}</div>
    <div v-else-if="!regions.length" class="chart-status">请选择区域</div>
    <v-chart v-else class="chart" :option="chartOption" autoresize />
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import api from '../api/config';
import { getStageColorByIndex } from '../utils/stageColors';

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
]);

const formatTimeLabel = (dateStr) => {
  const date = new Date(dateStr);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export default {
  name: 'MultiAreaStackChart',
  components: { VChart },
  props: {
    regions: {
      type: Array,
      default: () => [],
    },
    startTime: {
      type: String,
      required: true,
    },
    endTime: {
      type: String,
      required: true,
    },
    artists: {
      type: Array,
      default: () => [],
    },
    visibleStages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      chartTimes: [],
      chartOption: {
        animation: false,
        tooltip: { trigger: 'axis' },
        legend: { type: 'scroll', top: 0 },
        grid: { left: 8, right: 8, bottom: 48, top: 36, containLabel: true },
        dataZoom: [
          { type: 'slider', xAxisIndex: 0, height: 18, bottom: 8 },
          { type: 'inside', xAxisIndex: 0 },
        ],
        xAxis: { type: 'category', boundaryGap: false, data: [] },
        yAxis: { type: 'value', name: '人数' },
        series: [],
      },
    };
  },
  watch: {
    regions: {
      handler() {
        this.fetchAllAreaData();
      },
      deep: true,
      immediate: true,
    },
    startTime: 'fetchAllAreaData',
    endTime: 'fetchAllAreaData',
  },
  methods: {
    toApiDateTime(value) {
      if (!value) return null;
      const normalized = String(value).replace('T', ' ');
      if (normalized.length === 16) return `${normalized}:00`;
      return normalized;
    },
    extractMinutes(value) {
      if (!value) return 0;
      const str = String(value);
      const timePart = str.includes('T')
        ? str.split('T')[1]?.slice(0, 5)
        : str.includes(' ')
          ? str.split(' ')[1]?.slice(0, 5)
          : str.slice(0, 5);
      const [hours, minutes] = timePart.split(':').map(part => parseInt(part, 10) || 0);
      return hours * 60 + minutes;
    },
    getPerformingArtists(minutes) {
      const stageSet = new Set(this.visibleStages || []);
      const filterByStage = stageSet.size > 0;
      return (this.artists || [])
        .filter(artist => {
          if (!artist.startTime || !artist.endTime) return false;
          if (filterByStage && !stageSet.has(artist.stage)) return false;
          const start = this.extractMinutes(artist.startTime);
          const end = this.extractMinutes(artist.endTime);
          return minutes >= start && minutes <= end;
        })
        .sort((a, b) => a.startTime.localeCompare(b.startTime) || (a.stage || '').localeCompare(b.stage || ''));
    },
    formatArtistTooltipLines(artists) {
      if (!artists.length) return '';
      let html = '<br/><span style="color:#666;font-size:11px;">演出中</span><br/>';
      artists.forEach(artist => {
        const stage = artist.stage ? `${artist.stage} · ` : '';
        html += `${stage}${artist.name}<br/>`;
      });
      return html;
    },
    async fetchAllAreaData() {
      if (!this.regions.length || !this.startTime || !this.endTime) {
        this.chartTimes = [];
        this.chartOption = { ...this.chartOption, xAxis: { data: [] }, series: [] };
        return;
      }

      this.loading = true;
      this.error = null;

      const params = {
        startDate: this.toApiDateTime(this.startTime),
        endDate: this.toApiDateTime(this.endTime),
      };

      try {
        const results = await Promise.all(
          this.regions.map(async (region) => {
            const res = await api.get(`/data/area/${region.id}`, { params });
            return {
              id: region.id,
              label: region.label || region.id,
              data: (res.data.data || []).sort(
                (a, b) => new Date(a.raw_datetime) - new Date(b.raw_datetime)
              ),
            };
          })
        );

        this.buildChartOption(results);
      } catch (e) {
        console.error(e);
        this.error = '加载趋势数据失败';
      } finally {
        this.loading = false;
      }
    },
    buildChartOption(areaSeries) {
      const timeSet = new Set();
      areaSeries.forEach(({ data }) => {
        data.forEach(row => timeSet.add(row.raw_datetime));
      });

      const times = [...timeSet].sort((a, b) => new Date(a) - new Date(b));
      if (!times.length) {
        this.chartTimes = [];
        this.chartOption = {
          ...this.chartOption,
          xAxis: { ...this.chartOption.xAxis, data: [] },
          series: [],
        };
        return;
      }

      const xLabels = times.map(formatTimeLabel);
      this.chartTimes = times;
      const series = areaSeries.map((item, index) => {
        const lookup = {};
        item.data.forEach(row => {
          lookup[row.raw_datetime] = Number(row.area_count) || 0;
        });
        const color = getStageColorByIndex(index);
        return {
          name: item.label,
          type: 'line',
          stack: 'total',
          smooth: true,
          showSymbol: false,
          areaStyle: { opacity: 0.55 },
          lineStyle: { width: 1.5, color },
          itemStyle: { color },
          emphasis: { focus: 'series' },
          data: times.map(t => lookup[t] ?? 0),
        };
      });

      this.chartOption = {
        ...this.chartOption,
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const dataIndex = params[0]?.dataIndex;
            const hoverMinutes = dataIndex != null && this.chartTimes[dataIndex]
              ? this.extractMinutes(this.chartTimes[dataIndex])
              : this.extractMinutes(params[0]?.axisValue);
            let html = `${params[0]?.axisValue || ''}<br/>`;
            let sum = 0;
            params.forEach(p => {
              html += `${p.marker}${p.seriesName}: ${p.value}<br/>`;
              sum += Number(p.value) || 0;
            });
            html += `<strong>合计: ${sum}</strong>`;
            html += this.formatArtistTooltipLines(this.getPerformingArtists(hoverMinutes));
            return html;
          },
        },
        legend: {
          type: 'scroll',
          top: 0,
          data: areaSeries.map(s => s.label),
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xLabels,
        },
        series,
      };
    },
  },
};
</script>

<style scoped>
.multi-area-chart {
  margin-top: 0;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 4px 2px 2px;
}

.chart {
  height: 200px;
  width: 100%;
}

.chart-status {
  text-align: center;
  padding: 24px 8px;
  font-size: 12px;
  color: #999;
}

.chart-status.error {
  color: #f5222d;
}
</style>
